// Kernel-state helpers for PWA tenant isolation.
//
// The PWA at `app.khal.ai` serves every customer kernel (hv, homolog, …)
// from a single origin. Without scoping, zustand-persisted state (windows,
// tabs, etc.) bleeds across tenants. This module is the single source of
// truth for:
//
//   1. Deriving the active kernel's `orgId` from the `khal:enterprise:*`
//      localStorage entries written by the browser adapter.
//   2. Purging every other kernel's scoped keys when sign-in lands on a
//      different kernel than the one previously stored.
//
// It is a small, sync-only module so zustand/persist middleware can call
// `getActiveOrgId()` at module-init to build the store name.

const ENTERPRISE_LAST_USED_KEY = 'khal:enterprise:_lastUsed';

/** Fallback orgId when none can be derived (SSR, first boot, unparseable URL). */
export const DEFAULT_ORG_ID = 'default';

/** Prefixes of kernel-scoped zustand/persist stores. Extend as new stores adopt
 *  the scoping convention. */
const KERNEL_SCOPED_STORE_PREFIXES = ['khal-windows:', 'khal-desktop-tabs:'] as const;

/**
 * Read the most-recently-used kernel URL from localStorage and return its
 * first hostname label (e.g. `https://customer.example.com` → `"customer"`).
 *
 * Returns `DEFAULT_ORG_ID` when:
 *   - running server-side (no `window`/`localStorage`)
 *   - no `khal:enterprise:_lastUsed` entry exists yet (kernel picker state)
 *   - the stored URL is unparseable
 */
export function getActiveOrgId(): string {
	if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
		return DEFAULT_ORG_ID;
	}
	const lastUsed = window.localStorage.getItem(ENTERPRISE_LAST_USED_KEY);
	if (!lastUsed) return DEFAULT_ORG_ID;
	return orgIdFromServerUrl(lastUsed);
}

/**
 * Extract the orgId (first hostname label) from a kernel server URL. Mirrors
 * the logic in `src/desktop/providers/TauriKhalAuthProvider.tsx` so PWA and
 * Tauri agree on what the active orgId is for a given URL.
 */
export function orgIdFromServerUrl(serverUrl: string): string {
	try {
		const { hostname } = new URL(serverUrl);
		const first = hostname.split('.')[0];
		return first || DEFAULT_ORG_ID;
	} catch {
		return DEFAULT_ORG_ID;
	}
}

/**
 * Remove every kernel-scoped store entry whose orgId is not `exceptOrgId`.
 *
 * This is the primary guard for tenant isolation: whenever a new kernel
 * config is written, the adapter calls this to evict any prior kernel's
 * window/tab state so it cannot leak into the new session.
 *
 * Only keys matching one of `KERNEL_SCOPED_STORE_PREFIXES` are considered;
 * everything else in localStorage is left alone.
 */
export function purgeKernelScopedStorage(exceptOrgId: string): void {
	if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
	const toDelete: string[] = [];
	for (let i = 0; i < window.localStorage.length; i++) {
		const key = window.localStorage.key(i);
		if (!key) continue;
		for (const prefix of KERNEL_SCOPED_STORE_PREFIXES) {
			if (!key.startsWith(prefix)) continue;
			const org = key.slice(prefix.length);
			if (org !== exceptOrgId) toDelete.push(key);
			break;
		}
	}
	for (const key of toDelete) window.localStorage.removeItem(key);
}

/**
 * Build the canonical kernel-scoped store name for a given prefix and org.
 * Keeps the `:` separator consistent so `purgeKernelScopedStorage` can split
 * on it reliably.
 */
export function kernelScopedStoreName(prefix: string, orgId: string = getActiveOrgId()): string {
	return `${prefix}:${orgId}`;
}
